import { createTheme, ThemeProvider, ThemeOptions } from "@mui/material";

import { FC, PropsWithChildren } from "react";
import { NAV_BAR_HEIGHT } from "../Components/NavBar/navBarMetrics";
import { useThemeMode } from "./atom";
import { KawaiiOverlay } from "./KawaiiOverlay";

/**
 * App chrome that sits outside the normal MUI surfaces: the top toolbar and the
 * canvas the panels float on. These used to be hardcoded hex values scattered
 * around the components, which made a theme swap impossible.
 */
declare module "@mui/material/styles" {
  interface Palette {
    chrome: {
      /** Toolbar in production. Deliberately distinct from `navTest`. */
      navProd: string;
      /** Toolbar in dev/test. The "you are not in prod" signal. */
      navTest: string;
      /** Backdrop behind the panels. */
      canvas: string;
    };
  }
  interface PaletteOptions {
    chrome?: Palette["chrome"];
  }
}

/** Shared shape; the palettes are what actually differ. */
const base: ThemeOptions = {
  typography: {
    fontSize: 12,
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        dense: {
          height: NAV_BAR_HEIGHT,
          minHeight: NAV_BAR_HEIGHT,
        },
      },
    },
  },
};

/**
 * Tokyo Night: the editor colourscheme, as app chrome. Desaturated navy
 * surfaces, neon-adjacent syntax accents, flat panels edged with `fg_gutter`
 * rather than shadow.
 *
 * The surface ramp (canvas → default → paper) is stepped so panels read as
 * objects on a backdrop, and the accents are the canonical Tokyo Night hues,
 * all of which clear WCAG AA against both `background.paper` and
 * `background.default`.
 */
export const tokyoNightTheme = createTheme({
  ...base,
  typography: {
    ...base.typography,
    fontFamily: [
      "Roboto",
      "-apple-system",
      "BlinkMacSystemFont",
      "Helvetica Neue",
      "sans-serif",
    ].join(","),
    button: {
      textTransform: "none",
      letterSpacing: "0.02em",
    },
  },
  shape: {
    borderRadius: 6,
  },
  palette: {
    mode: "dark",
    primary: {
      // `blue` — the colourscheme's function/identifier hue, and its de facto
      // primary.
      main: "#7AA2F7",
    },
    secondary: {
      // `magenta`
      main: "#BB9AF7",
    },
    info: {
      // `cyan`
      main: "#7DCFFF",
    },
    success: {
      // `green`
      main: "#9ECE6A",
    },
    warning: {
      // `yellow`
      main: "#E0AF68",
    },
    error: {
      // `red`
      main: "#F7768E",
    },
    background: {
      // `bg` and the Storm surface, so raised panels separate from the app
      // background without an elevation overlay.
      default: "#1A1B26",
      paper: "#24283B",
    },
    text: {
      // `fg` / `fg_dark`
      primary: "#C0CAF5",
      secondary: "#A9B1D6",
    },
    // `fg_gutter` — the colourscheme's own border colour.
    divider: "#3B4261",
    chrome: {
      navProd: "#1F2335",
      // `blue0`. Clearly not the near-black prod bar at a glance, which is the
      // whole job of this colour.
      navTest: "#3D59A1",
      // `bg_dark` — darkest step in the ramp, so floating panels have an edge
      // without needing a drop shadow.
      canvas: "#16161E",
    },
  },
  components: {
    ...base.components,
    MuiPaper: {
      styleOverrides: {
        root: {
          // MUI tints dark-mode Paper with an elevation gradient. That would
          // drift every surface off-palette, so the ramp above does the work
          // instead and panels take an explicit hairline.
          backgroundImage: "none",
          border: "1px solid #3B4261",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
      },
    },
  },
});

/**
 * The opposite of Tokyo Night, on purpose. Nyan cat, fairy lights, and as much
 * Japanese emoji as the frame will hold. Still `mode: "dark"` so every MUI
 * component keeps its light-on-dark text and stays legible — the chaos is
 * decoration, not a functional regression.
 *
 * Motion is suppressed under `prefers-reduced-motion`; see KawaiiOverlay.
 */
export const kawaiiTheme = createTheme({
  ...base,
  typography: {
    ...base.typography,
    fontFamily: [
      "Comic Sans MS",
      "Chalkboard SE",
      "Comic Neue",
      "Roboto",
      "cursive",
    ].join(","),
    button: {
      textTransform: "none",
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 14,
  },
  palette: {
    mode: "dark",
    primary: {
      main: "#FF4FD8",
    },
    secondary: {
      main: "#22E0FF",
    },
    info: {
      main: "#7CFFCB",
    },
    success: {
      main: "#8BFF6B",
    },
    warning: {
      main: "#FFE14D",
    },
    error: {
      main: "#FF5C8A",
    },
    background: {
      default: "#1B0B2F",
      paper: "#2C1249",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#FFD9F5",
    },
    divider: "rgba(255, 121, 226, 0.35)",
    chrome: {
      navProd: "#FF2D95",
      navTest: "#7A2DFF",
      canvas: "#120722",
    },
  },
  components: {
    ...base.components,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage:
            "linear-gradient(160deg, rgba(255,79,216,0.10), rgba(34,224,255,0.10))",
          border: "1px solid rgba(255,121,226,0.35)",
          boxShadow: "0 0 12px rgba(255,79,216,0.25)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
  },
});

export const ThemeProviderWrapper: FC<PropsWithChildren> = ({ children }) => {
  const [themeMode] = useThemeMode();

  const isKawaii = themeMode === "kawaii";

  return (
    <ThemeProvider theme={isKawaii ? kawaiiTheme : tokyoNightTheme}>
      {children}
      {isKawaii && <KawaiiOverlay />}
    </ThemeProvider>
  );
};
